import { Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSceneObjectTypes } from "../SceneObjectTypesContext";
import type {
  ControlNode as ControlNodeType,
  Port,
} from "../../../../util/src/blueprint/types.js";
import { VARIABLE_VALUE_PORT_ID } from "../../../../util/src/blueprint/variable-graph.js";
import { PortHandle } from "../PortHandle.js";
import { VariablePortTypeMenu } from "./VariablePortMenus.js";
import dico from "../../../../util/src/language/dico.js";
import OutputIcon from "@mui/icons-material/Output";
import React, { useCallback, useState } from "react";

export interface VariableEndNodeProps {
  node: ControlNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onChangeValueType?: (valueType: string) => void;
  readOnly?: boolean;
}

/**
 * Exit terminal for scene variable graphs.
 * Single value input port; right-click opens a type picker.
 */
const VariableEndNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onChangeValueType,
  readOnly = false,
}: VariableEndNodeProps) => {
  const behaviorDico = dico().behavior;
  const catalog = useSceneObjectTypes();
  const valuePort = node.ports.find(
    (port) => port.id === VARIABLE_VALUE_PORT_ID,
  );

  const [typeMenuAnchor, setTypeMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const handlePortContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (readOnly || !onChangeValueType) return;
      event.preventDefault();
      event.stopPropagation();
      setTypeMenuAnchor(event.currentTarget as HTMLElement);
    },
    [onChangeValueType, readOnly],
  );

  if (!valuePort) return null;

  return (
    <div
      style={{
        minWidth: 200,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background:
            "linear-gradient(180deg, #c4b5fd 0%, #7c3aed 55%, #5b21b6 100%)",
        }}
      >
        <OutputIcon sx={{ fontSize: 14 }} />
        {behaviorDico.controls?.variableEnd ?? ""}
      </div>
      {onChangeValueType && (
        <Button
          size="small"
          disabled={readOnly}
          endIcon={<ExpandMoreIcon />}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setTypeMenuAnchor(event.currentTarget);
          }}
          sx={{ mx: 1, fontSize: 11, textTransform: "none" }}
        >
          {behaviorDico.sceneCatalog.objectEditor.valueType}:{" "}
          {catalog?.objectTypes?.find((t) => t.id === valuePort.objectTypeId)
            ?.name ??
            (behaviorDico.nodeList.inputTypes as Record<string, string>)[
              valuePort.valueType
            ] ??
            valuePort.valueType}
        </Button>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 8,
          minHeight: 36,
          padding: "6px 8px 10px",
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onContextMenu={handlePortContextMenu}
      >
        <PortHandle
          node={node}
          port={valuePort}
          setPortsMap={setPortsMap}
          onMouseDown={() => onPortMouseDown?.(node.id, valuePort)}
          onMouseUp={() => onPortMouseUp?.(node.id, valuePort)}
          isConnected={isPortConnected?.(node.id, valuePort.id) ?? false}
          onContextMenu={handlePortContextMenu}
        />
      </div>
      {onChangeValueType ? (
        <VariablePortTypeMenu
          anchorEl={typeMenuAnchor}
          open={Boolean(typeMenuAnchor)}
          currentValueType={
            valuePort.objectTypeId
              ? valuePort.valueType + "#" + valuePort.objectTypeId
              : valuePort.valueType
          }
          onClose={() => setTypeMenuAnchor(null)}
          onSelect={onChangeValueType}
        />
      ) : null}
    </div>
  );
};

export const VariableEndNode = React.memo(VariableEndNodeComponent);
